import { ReactKeycloakProvider } from "@react-keycloak/web";
import Keycloak from "keycloak-js";
import { initKeycloak, COMPANY_STORAGE_KEY } from "./Keycloak";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import AdminWrapper from "./component/AdminWrapper";
import CompanyPage from "./component/CompanyPage";
import { Toaster } from "sonner";
import './App.css'
import { lazy, Suspense, useState } from "react";
import { CircularProgress, Box, ThemeProvider, CssBaseline } from "@mui/material";
import theme from "./theme";

const AdminPage = lazy(() => import("./component/AdminPage"));
const MyTransactions = lazy(
  () => import("./component/myTransactions/MyTransactions")
);

// Initialize from sessionStorage on first load (runs once at module level).
// This survives reloads within the current tab/session — needed because the
// Keycloak login redirect navigates away and back — but never persists past
// logout or a closed tab (see COMPANY_STORAGE_KEY).
//
// Landing back on the start URL ("/" — localhost or partenaire.bictorys.com
// with no path) always wipes it and forces CompanyPage again, even mid-session,
// since that's the user re-entering the app from scratch rather than continuing
// an in-progress login redirect (which lands on "/admin").
const _isStartUrl = window.location.pathname === '/';
if (_isStartUrl) {
  sessionStorage.removeItem(COMPANY_STORAGE_KEY);
}
const _savedCompany = _isStartUrl ? null : sessionStorage.getItem(COMPANY_STORAGE_KEY);
const _initialKeycloak: Keycloak | null = _savedCompany
  ? initKeycloak(_savedCompany)
  : null;

const RouteFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "60vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const App = () => {
  const [keycloakInstance, setKeycloakInstance] = useState<Keycloak | null>(
    _initialKeycloak
  );

  const handleCompanySet = (company: string) => {
    setKeycloakInstance(initKeycloak(company));
  };

  if (!keycloakInstance) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CompanyPage onCompanySet={handleCompanySet} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ReactKeycloakProvider
        authClient={keycloakInstance}
        initOptions={{
          onLoad: "check-sso",
          flow: "standard",
          pkceMethod: "S256",
          checkLoginIframe: false,
          enableLogging: import.meta.env.DEV,
          // Pin the callback target so it's always "/admin", never a race
          // between this and the "/" -> "/admin" client-side redirect —
          // App.tsx's start-URL check below relies on that being stable.
          redirectUri: `${window.location.origin}/admin`,
        }}
        autoRefreshToken
      >
        <Router>
          <AdminWrapper>
            <Toaster richColors position="top-center" closeButton duration={Infinity} />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Navigate to="/admin" replace />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/my-transactions" element={<MyTransactions />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </Suspense>
          </AdminWrapper>
        </Router>
      </ReactKeycloakProvider>
    </ThemeProvider>
  );
};

export default App;
